import {createForm, FormError, getValue, setResponse, setValue, SubmitHandler, valiForm} from "@modular-forms/solid";
import {useAction, useNavigate} from "@solidjs/router";
import * as v from "valibot";
import {
    ButtonGroup,
    CheckboxInputField,
    FieldGroup,
    FormContainer,
    FormResponse,
    InputField,
    PasswordInputField
} from "~/components/form/ui/form";
import {register} from "~/sdk/member";
import {Button} from "~/components/ui/Button";
import {formatDate, subYears} from "date-fns";

const RegisterSchema = v.pipe(v.object({
        firstName: v.string(),
        lastName: v.string(),
        phone: v.string(),
        dob: v.pipe(
            v.date(),
            v.maxValue(subYears(new Date(), 18), "You must be at least 18 years old")
        ),
        email: v.pipe(
            v.string(),
            v.nonEmpty('Please enter your email.'),
            v.email('The email address is badly formatted'),
        ),
        password: v.pipe(
            v.string(),
            v.nonEmpty('Please enter your password.'),
            v.minLength(8, 'Your password must be at least 8 characters long'),
        ),
        confirmPassword: v.string(),
        emergencyName: v.string(),
        emergencyPhone: v.string(),
        healthInformation: v.string(),
        marketing: v.boolean(),
        disclosure: v.pipe(
            v.boolean(),
            v.value(true, 'You must accept the disclosure')
        ),
    }), v.forward(
        v.partialCheck(
            [['password'], ['confirmPassword']],
            (input) => input.password === input.confirmPassword,
            'The password do not match.'
        ), ['confirmPassword']
    )
)

type RegisterForm = v.InferInput<typeof RegisterSchema>

export default function RegisterForm(props: {
    email?: string,
    redirect?: string,
}) {

    const [form, Form] = createForm<RegisterForm>({
        validate: valiForm(RegisterSchema),
        initialValues: {
            email: props.email,
        }
    })

    const navigate = useNavigate();
    const registerAction = useAction(register)

    const handleSubmit: SubmitHandler<RegisterForm> = async (values, event) => {
        setResponse(form, {status: "info", message: "Registering..."})
        try {
            await registerAction(values)
        } catch (e) {
            throw new FormError<RegisterForm>(String(e))
        }
        setResponse(form, {status: "success", message: "Successfully registered"});
        navigate(props.redirect ? props.redirect : "/account", {replace: true})
    }

    const email = () => getValue(form, "email")

    return (
        <FormContainer>
            <h1>Register</h1>
            <p>Your wellbeing matters to us. Create your account and complete a short health form once - then simply log
                in for easy future bookings.</p>
            <hr/>
            <Form.Form onSubmit={handleSubmit}>
                <FieldGroup title="Personal Information">
                    <Form.Field name="firstName">
                        {(field, props) =>
                            <InputField {...props} type="text" label="First Name" value={field.value}
                                        error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="lastName">
                        {(field, props) =>
                            <InputField {...props} type="text" label="Last Name" value={field.value} error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="dob" type="Date">
                        {(field, props) =>
                            <InputField {...props} type="date" label="Date of Birth"
                                        value={field.value ? formatDate(field.value!, 'yyyy-MM-dd') : ""}
                                        error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="email">
                        {(field, props) =>
                            <InputField {...props} type="email" label="Email" value={field.value} error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="phone">
                        {(field, props) =>
                            <InputField {...props} type="tel" label="Phone" value={field.value} error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="password">
                        {(field, props) =>
                            <PasswordInputField {...props} label="Password" value={field.value} error={field.error}
                                                required/>
                        }
                    </Form.Field>
                    <Form.Field name="confirmPassword">
                        {(field, props) =>
                            <PasswordInputField {...props} label="Confirm Password" value={field.value}
                                                error={field.error}
                                                required/>
                        }
                    </Form.Field>
                </FieldGroup>
                <FieldGroup title="Emergency Contact">
                    <Form.Field name="emergencyName">
                        {(field, props) =>
                            <InputField {...props} type="text" label="Name" value={field.value}
                                        error={field.error}
                                        required/>
                        }
                    </Form.Field>
                    <Form.Field name="emergencyPhone">
                        {(field, props) =>
                            <InputField {...props} type="tel" label="Phone" value={field.value}
                                        error={field.error}
                                        required/>
                        }
                    </Form.Field>
                </FieldGroup>
                <FieldGroup>
                    <span></span>
                    <Form.Field name="healthInformation">
                        {(field, props) =>
                            <InputField {...props} type="text"
                                        label="Do you have any health conditions I should be aware of that effects you practicing yoga?"
                                        placeholder=""
                                        value={field.value}
                                        error={field.error}/>
                        }
                    </Form.Field>
                </FieldGroup>
                <FieldGroup>
                    <Form.Field name="disclosure" type="boolean">
                        {(field, props) =>
                            <CheckboxInputField {...props} value={field.value ? "on" : "off"} error={field.error}
                                                label={<Disclosure/>}
                                                checked={!!field.value}
                                                onCheckedChange={(d) => {
                                                    setValue(form, "disclosure", d.checked === true)
                                                }}
                            />
                        }
                    </Form.Field>
                    <Form.Field name="marketing" type="boolean">
                        {(field, props) =>
                            <CheckboxInputField {...props} value={field.value ? "on" : "off"} error={field.error}
                                                label={"Do you want to receive offers, updates via email? (You can unsubscribe at any time)"}
                                                checked={!!field.value}
                                                onCheckedChange={(d) => {
                                                    setValue(form, "marketing", d.checked === true)
                                                }}
                            />
                        }
                    </Form.Field>
                </FieldGroup>
                <p>By booking, you accept these terms and agree to raise any health concerns with the teacher.</p>
                <FormResponse response={form.response}/>
                <ButtonGroup>
                    <Button type="submit" variant="primarySolid">Register</Button>
                </ButtonGroup>
            </Form.Form>
        </FormContainer>
    )
}

function Disclosure() {
    return (
        <span>
        <strong>Disclosure</strong>
        <br/>
        By participating in any Graceful Space Yoga classes, I confirm
        that I am choosing to engage in yoga voluntarily and understand
        that, as with any physical activity, there is a potential risk
        of injury. I accept full responsibility for my health and
        wellbeing throughout the class.
        <br/>
        I acknowledge that it is my responsibility to consult with a
        doctor before participating, particularly if I have any
        pre-existing medical conditions, injuries, or other health
        concerns.
        <br/>
        I understand that participation in Graceful Space Yoga classes
        requires me to be 18 years of age or older.
        I understand that participation is not permitted if I am
        currently pregnant or less than 12 weeks post-partum.
        I understand that these sessions are not a substitute for
        medical counselling, diagnosis, or treatment.
        <br/>
        By attending any Graceful Space Yoga classes, I consent to take
        full responsibility for my own wellbeing and any injury
        sustained, and I release Graceful Space Yoga and its instructors
        from all liability.
    </span>
    )
}